import { getLocale, t } from './i18n';
import type { PlayerWithStats } from './types';
import type { GameFull } from './types-games';
import { buildPlayerCardElement } from './card';
import { apiRequest } from './api';
import { state } from './state';
import zektonTtfUrl from '../assets/fonts/zekton-rg.ttf';

// ============================================================
// Шаринг: рисуем картинку и скачиваем PNG.
// Никакого системного шеринга — просто файл в загрузки.
// Карточка игрока не перерисовывается вручную: снимаем тот же DOM,
// что на сайте и в оверлее (buildPlayerCardElement).
// ============================================================

const WIDTH = 1200;
const HEIGHT = 630;
const BG = '#0b0f14';
const TEXT = '#e0e6ed';
const MUTED = '#8899aa';
const GOLD = '#f1c40f';

const RACE_COLORS: Record<string, string> = {
  T: '#3498db',
  Z: '#9b59b6',
  P: '#f1c40f',
  R: '#95a5a6',
};

/** Тост внизу экрана («картинка сохранена»). */
function showToast(message: string): void {
  let toast = document.querySelector<HTMLElement>('.share-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'share-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  requestAnimationFrame(() => toast?.classList.add('is-visible'));
  window.clearTimeout((toast as HTMLElement & { __timer?: number }).__timer);
  (toast as HTMLElement & { __timer?: number }).__timer = window.setTimeout(() => {
    toast?.classList.remove('is-visible');
  }, 2200);
}

// ============================================================
// Карточка игры: фон под карту, победители, состав, MVP и реакции.
// ============================================================

const GREEN = '#2ecc71';
const PANEL_BG = 'rgba(255, 255, 255, 0.025)';
const PANEL_BORDER = 'rgba(255, 255, 255, 0.09)';

/** Скруглённый прямоугольник — вручную, без roundRect (поддержка старее). */
function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Подпись-заголовок блока: мелкие капсы с разрядкой. */
function drawHeading(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  ctx.save();
  ctx.fillStyle = MUTED;
  ctx.font = '700 12px Zekton, sans-serif';
  ctx.textBaseline = 'top';
  if ('letterSpacing' in ctx) ctx.letterSpacing = '2px';
  ctx.fillText(text.toUpperCase(), x, y);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
  ctx.restore();
}

/** Картинка заполняет прямоугольник целиком (object-fit: cover). */
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/** Фон: затемнённая (и размытая) карта, золотое свечение, сетка, виньетка. */
function drawGameBackground(
  ctx: CanvasRenderingContext2D,
  mapImage: HTMLImageElement | null,
): void {
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
  gradient.addColorStop(0, '#101823');
  gradient.addColorStop(0.55, BG);
  gradient.addColorStop(1, '#070a0e');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Карта как атмосферная подложка — детали не читаются, нужен только цвет.
  if (mapImage) {
    ctx.save();
    ctx.filter = 'blur(26px) saturate(0.85)';
    drawImageCover(ctx, mapImage, 0, 0, WIDTH, HEIGHT);
    ctx.restore();
    ctx.fillStyle = 'rgba(7, 10, 14, 0.84)';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
  }

  const glow = ctx.createRadialGradient(WIDTH - 120, -80, 0, WIDTH - 120, -80, 620);
  glow.addColorStop(0, 'rgba(241, 196, 15, 0.18)');
  glow.addColorStop(1, 'rgba(241, 196, 15, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
  ctx.lineWidth = 1;
  for (let x = 0; x < WIDTH; x += 48) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, HEIGHT);
    ctx.stroke();
  }
  for (let y = 0; y < HEIGHT; y += 48) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(WIDTH, y);
    ctx.stroke();
  }

  const vignette = ctx.createRadialGradient(WIDTH / 2, HEIGHT / 2, 180, WIDTH / 2, HEIGHT / 2, 760);
  vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
  vignette.addColorStop(1, 'rgba(0, 0, 0, 0.55)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  ctx.fillStyle = GOLD;
  ctx.fillRect(0, 0, WIDTH, 5);
}

function drawBrand(ctx: CanvasRenderingContext2D, subtitle: string): void {
  ctx.fillStyle = GOLD;
  ctx.font = '600 20px Zekton, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText('SC2 FFA LEAGUE', 64, 44);

  ctx.fillStyle = MUTED;
  ctx.font = '400 15px Zekton, sans-serif';
  ctx.fillText(subtitle, 64, 74);
}

function drawFooter(ctx: CanvasRenderingContext2D, link: string): void {
  ctx.fillStyle = MUTED;
  ctx.font = '400 16px Zekton, sans-serif';
  ctx.textBaseline = 'bottom';
  ctx.fillText(link, 64, HEIGHT - 40);
}

function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let result = text;
  while (result.length > 1 && ctx.measureText(`${result}…`).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

function makeCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas is not supported');
  return { canvas, ctx };
}

/** Скачать PNG в загрузки. */
function downloadImage(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
  showToast(t('share.downloaded'));
}

// ============================================================
// Карточка игрока: живой DOM → SVG-снимок → canvas → PNG.
// ============================================================

/** Эталонная раскладка карточки (десктопная, 16:9). */
const CARD_W = 960;
const CARD_H = 540;
/** Итоговый размер картинки — карточка просто масштабируется векторно. */
const CARD_OUT_W = 1200;
const CARD_OUT_H = 675;

const dataUrlCache = new Map<string, Promise<string>>();

/** Файл → data: URL: внутри SVG-картинки внешние ссылки не загружаются. */
function toDataUrl(url: string): Promise<string> {
  const cached = dataUrlCache.get(url);
  if (cached) return cached;
  const promise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error ?? new Error('read failed'));
          reader.readAsDataURL(blob);
        }),
    );
  // Не кэшируем ошибку — иначе повтор не сработает.
  promise.catch(() => dataUrlCache.delete(url));
  dataUrlCache.set(url, promise);
  return promise;
}

/**
 * Правило относится к карточке?
 * Псевдоэлементы (::before) проверяем по базовому селектору, состояния
 * (:hover) отбрасываем — на статичной картинке их нет.
 */
function ruleMatchesCard(selectorText: string, root: HTMLElement): boolean {
  return selectorText.split(',').some((raw) => {
    let sel = raw.trim();
    if (!sel) return false;
    if (sel === ':root') return true; // переменные темы нужны карточке
    sel = sel.split('::')[0] ?? sel;
    sel = sel.replace(
      /:(hover|focus|focus-visible|focus-within|active|visited|disabled|checked|target)\b/g,
      '',
    );
    if (!sel.trim()) return false;
    try {
      return root.matches(sel) || root.querySelector(sel) !== null;
    } catch {
      return false; // селектор, который не понимает querySelector
    }
  });
}

/** Только те правила из подключённых стилей, что реально касаются карточки. */
function collectCardCss(root: HTMLElement): string {
  const out: string[] = [];
  const walk = (rules: CSSRuleList): void => {
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSStyleRule && ruleMatchesCard(rule.selectorText, root)) {
        out.push(rule.cssText);
      }
      // @media намеренно пропускаем: в 960px-раскладке нужен «широкий» вариант,
      // а внутри SVG запросы считались бы по его viewport (1200px).
    }
  };
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      walk(sheet.cssRules);
    } catch {
      // кросс-доменный стиль — просто пропускаем
    }
  }
  return out.join('\n');
}

/** Экранирование для XML-атрибута: значения вида "Zekton" ломают разметку. */
function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** CSS-переменные из :root — переносим инлайном (в SVG :root уже не тот). */
function rootVariables(): string {
  const style = getComputedStyle(document.documentElement);
  const parts: string[] = [];
  for (let i = 0; i < style.length; i++) {
    const name = style.item(i);
    if (!name.startsWith('--')) continue;
    const value = style.getPropertyValue(name).trim();
    if (value) parts.push(`${name}:${value};`);
  }
  return parts.join('');
}

/** SVG-строка → картинка (data: URL, чтобы canvas не «портился»). */
function svgToImage(svg: string): Promise<HTMLImageElement> {
  const img = new Image();
  return new Promise<HTMLImageElement>((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('svg rasterization failed'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

/** Снимок карточки игрока в canvas: живой DOM рендерим в отдельном iframe. */
async function capturePlayerCard(player: PlayerWithStats): Promise<HTMLCanvasElement> {
  const card = buildPlayerCardElement(player);

  // Иконки рас — в data URL (в снимке внешние картинки не подгрузятся).
  await Promise.all(
    Array.from(card.querySelectorAll('img')).map(async (img) => {
      const src = img.getAttribute('src');
      if (!src) return;
      try {
        img.setAttribute('src', await toDataUrl(src));
      } catch {
        img.removeAttribute('src');
      }
    }),
  );

  const css = collectCardCss(card);
  const font = await toDataUrl(zektonTtfUrl);
  const vars = rootVariables();

  // Ширина iframe фиксирована, поэтому медиазапросы карточки дают десктопную
  // раскладку, даже если пользователь открыл сайт с телефона.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  iframe.style.cssText =
    `position:fixed;left:-30000px;top:0;width:${CARD_W}px;height:${CARD_H}px;` +
    'border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('iframe document is not available');

    doc.open();
    doc.write(
      '<!doctype html><html><head><meta charset="utf-8"><style>' +
        `@font-face{font-family:'Zekton';src:url('${font}') format('truetype');font-weight:400;font-style:normal;}` +
        'html,body{margin:0;padding:0;}' +
        `#share-root{width:${CARD_W}px;height:${CARD_H}px;}` +
        css +
        '</style></head><body><div id="share-root"></div></body></html>',
    );
    doc.close();

    const root = doc.getElementById('share-root');
    if (!root) throw new Error('share root is not available');
    root.appendChild(doc.adoptNode(card));

    await doc.fonts?.ready;

    // Резерв под титулы считаем уже в десктопной раскладке.
    const titles = card.querySelector<HTMLElement>('.pc-titles');
    if (titles) card.style.setProperty('--titles-reserve', `${titles.offsetWidth}px`);

    card.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    const markup = new XMLSerializer().serializeToString(card);
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${CARD_OUT_W}" height="${CARD_OUT_H}"` +
      ` viewBox="0 0 ${CARD_W} ${CARD_H}">` +
      `<foreignObject x="0" y="0" width="${CARD_W}" height="${CARD_H}">` +
      `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${CARD_W}px;height:${CARD_H}px;${escapeXmlAttr(vars)}">` +
      `<style xmlns="http://www.w3.org/1999/xhtml"><![CDATA[${css.replace(/]]>/g, ']]]]><![CDATA[>')}]]></style>` +
      markup +
      '</div></foreignObject></svg>';

    const img = await svgToImage(svg);
    const canvas = document.createElement('canvas');
    canvas.width = CARD_OUT_W;
    canvas.height = CARD_OUT_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas is not supported');
    ctx.drawImage(img, 0, 0, CARD_OUT_W, CARD_OUT_H);
    return canvas;
  } finally {
    iframe.remove();
  }
}

/** Карточка игрока — та же, что на сайте/в оверлее, скачивается как PNG. */
export async function sharePlayerCard(player: PlayerWithStats): Promise<void> {
  try {
    await document.fonts?.ready;
    const canvas = await capturePlayerCard(player);
    const blob = await toBlob(canvas);
    downloadImage(blob, `player-${player.name.replace(/[^\w-]+/g, '_')}.png`);
    // Лог действий: только для вошедших, best-effort — не мешает скачиванию.
    if (state.token) {
      void apiRequest(`/api/players/${player.id}/share`, {
        method: 'POST',
        token: state.token,
      }).catch(() => undefined);
    }
  } catch (err) {
    console.error('[share] player card failed:', err);
    showToast(t('share.error'));
  }
}

/** Место игрока в игре — как в списке игр (1 — победитель). */
function gamePlace(game: GameFull, player: GameFull['players'][number]): number {
  if (player.is_winner) return 1;
  if (player.eliminated_at != null) return game.players.length - player.eliminated_at + 1;
  return 2;
}

/** Карточка игры: карта, победители, состав с местами. */
export async function shareGameCard(game: GameFull): Promise<void> {
  try {
    await document.fonts?.ready;
    const { canvas, ctx } = makeCanvas();
    drawGameBackground(ctx, null);
    drawBrand(ctx, t('share.game_title'));

    const mapName = game.map?.name ?? '—';

    ctx.textBaseline = 'top';
    ctx.fillStyle = GOLD;
    ctx.font = '700 50px Zekton, sans-serif';
    ctx.fillText(truncate(ctx, mapName, 760), 64, 118);

    const date = new Date(game.played_at).toLocaleString(
      getLocale() === 'ru' ? 'ru-RU' : 'en-US',
      { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
    );
    const metaParts = [
      date,
      game.format?.name ?? null,
      game.map?.aka ?? null,
      game.duration_min ? `${game.duration_min} ${t('games.minutes_short')}` : null,
      `${t('games.field_players')}: ${game.players.length}`,
    ].filter(Boolean) as string[];

    ctx.fillStyle = MUTED;
    ctx.font = '400 17px Zekton, sans-serif';
    ctx.fillText(truncate(ctx, metaParts.join(' · '), 1000), 64, 182);

    // --- Победители: отдельная панель, чтобы результат читался сразу ---
    const winners = game.players.filter((p) => p.is_winner);
    const panelY = 226;
    const panelH = 92;
    roundRectPath(ctx, 64, panelY, WIDTH - 128, panelH, 8);
    ctx.fillStyle = PANEL_BG;
    ctx.fill();
    ctx.strokeStyle = PANEL_BORDER;
    ctx.lineWidth = 1;
    ctx.stroke();

    drawHeading(ctx, t('games.winner_label'), 88, panelY + 18);
    ctx.fillStyle = winners.length > 0 ? GOLD : TEXT;
    ctx.font = '700 32px Zekton, sans-serif';
    ctx.fillText(
      truncate(ctx, winners.map((w) => w.player_name).join(' + ') || '—', WIDTH - 200),
      88,
      panelY + 42,
    );

    // --- Состав: колонки с расой и местом ---
    drawHeading(ctx, t('games.field_players'), 64, 350);
    ctx.font = '400 18px Zekton, sans-serif';
    const perColumn = Math.max(1, Math.ceil(game.players.length / 2));
    const rowH = Math.min(34, Math.floor((HEIGHT - 420) / perColumn));

    game.players.forEach((player, index) => {
      const column = index < perColumn ? 0 : 1;
      const row = index < perColumn ? index : index - perColumn;
      const x = 64 + column * 540;
      const y = 382 + row * rowH;
      if (y > HEIGHT - 80) return;

      const place = gamePlace(game, player);
      const placeColor = place === 1 ? GOLD : place <= 3 ? GREEN : MUTED;

      ctx.fillStyle = placeColor;
      ctx.font = '700 15px Zekton, sans-serif';
      ctx.fillText(`#${place}`, x, y);

      ctx.fillStyle = RACE_COLORS[player.race] ?? RACE_COLORS.R!;
      ctx.fillRect(x + 42, y + 4, 10, 10);

      ctx.fillStyle = player.is_winner ? GOLD : TEXT;
      ctx.font = `${player.is_winner ? '700' : '400'} 18px Zekton, sans-serif`;
      ctx.fillText(truncate(ctx, player.player_name, 400), x + 62, y);
    });

    drawFooter(ctx, 'sc2-ffa-league.ru');

    const blob = await toBlob(canvas);
    downloadImage(blob, `game-${game.id}.png`);
    if (state.token) {
      void apiRequest(`/api/games/${game.id}/share`, {
        method: 'POST',
        token: state.token,
      }).catch(() => undefined);
    }
  } catch (err) {
    console.error('[share] game card failed:', err);
    showToast(t('share.error'));
  }
}
